import React, { memo, useEffect, useContext } from 'react';
import useWebBlocksApi from '../../hooks/useWebBlocksApi';
import { useAntiFlickering } from '../../hooks/useAntiFlickering';
import { useValidate } from '../../hooks/useValidate';
import { ScriptTagContext } from '../../context/ScriptTagContext';
import { PreviewContext } from '../../context/PreviewQueryContext';
import { LoggerContext } from '../../context/LoggerContext';
import { getBlocks } from '../../api/blocksApi';
import {
	BlockInfoType,
	WebBlocksApiBlockT,
	WebBlocksApiNormalBlockT,
} from '../../lib/REST/types';
import { BlockReplacer } from '../BlockReplacer';
import { parseLog } from '../Logger/utils';
import {
	getCreatePortalKey,
	getMainAppInfo,
	onRendered,
	onAppear,
	filterDuplicates,
	onLinkClick,
} from './utils';
import { blockSchema } from './blockSchema';
import { useSendApiTrackingToRat, TrackAppLoad } from '../../lib/RAT';
import { PostLoadEvent } from '../../lib/REST/ApiTracking';
import { Script } from '../AppUserInterface/Script';

const App = function App({ environment = '' }: { environment: string }) {
	const scriptData = useContext(ScriptTagContext);
	const previewQuery = useContext(PreviewContext);
	const { updateGlobalLog } = useContext(LoggerContext);

	const { clientId, ratAccountId, ratServiceType, backupSdkSrc } = scriptData;

	const [response, trigger] = useWebBlocksApi(getBlocks);
	const { isFetching, error, result } = response;
	const { sessionId, isMobile, location, userId } = getMainAppInfo();

	useEffect(() => {
		updateGlobalLog({
			isFetching,
			error,
		});
	}, [isFetching, error]);

	const { valid: validBlocks, invalid: invalidBlocks } =
		useValidate<WebBlocksApiBlockT>(result || [], blockSchema, updateGlobalLog);

	const { onBlockLoad } = useAntiFlickering(result, scriptData, Boolean(error));

	useEffect(() => {
		const invalidNormalBlocks = invalidBlocks.filter(
			(t) => t.selector
		) as WebBlocksApiNormalBlockT[];
		invalidNormalBlocks.forEach((invalidBlock) =>
			onBlockLoad(invalidBlock.selector)
		);
	}, [invalidBlocks]);

	useSendApiTrackingToRat(response, ratAccountId, ratServiceType, isMobile);

	useEffect(() => {
		if (!clientId) return;

		if (clientId) {
			const load = async () => {
				try {
					await TrackAppLoad({
						ratAccountId,
						ratServiceType,
						isMobile,
						trackCb: () =>
							PostLoadEvent({
								apiEnv: environment,
								clientId,
								sessionId,
								url: location,
								previewQuery,
							}),
					});
				} catch (e) {
					// In case load event fails web-blocks has to replace content
					// since the load event was removed in the next version, we don't need any error handling in this catch
				}

				await trigger({
					environment,
					clientId,
					location,
					isMobile,
					sessionId,
					userId,
					previewQuery,
				});
			};
			load();
		}
	}, [clientId, environment, previewQuery]);

	if (error && backupSdkSrc) {
		const body = document.body || document.getElementsByTagName('body')[0];
		return <Script url={backupSdkSrc} container={body} />;
	}

	if (!result) {
		return null;
	}

	const blocks = filterDuplicates({ blocks: validBlocks });

	return (
		// eslint-disable-next-line react/jsx-no-useless-fragment
		<>
			{blocks?.map(
				({ contentId, campaignId, selector, component, customJs }) => {
					// NOTE: Typescript wrongly merges two types so doesn't understand that !selector => customJs !== undefined
					const blockInfo = {
						component,
						selector,
						customJs,
					} as BlockInfoType;
					return (
						<BlockReplacer
							{...blockInfo}
							getComment={(prevHTML: string) =>
								parseLog({
									campaignId,
									contentId,
									selector,
									prevHTML,
								})
							}
							onAppear={() =>
								onAppear({
									campaignId,
									contentId,
									ratAccountId,
									ratServiceType,
									isMobile,
									environment,
									clientId,
									sessionId,
									previewQuery,
								})
							}
							key={getCreatePortalKey({
								contentId,
								campaignId,
								selector,
							})}
							onRendered={() =>
								onRendered({
									onBlockLoad,
									selector,
									campaignId,
									contentId,
									ratAccountId,
									ratServiceType,
									isMobile,
									environment,
									clientId,
									sessionId,
									previewQuery,
								})
							}
							onLinkClick={(url: string) =>
								onLinkClick({
									environment,
									clientId,
									sessionId,
									campaignId,
									contentId,
									url,
									previewQuery,
									ratAccountId,
									ratServiceType,
									isMobile,
								})
							}
						/>
					);
				}
			)}
		</>
	);
};

export const MainApp = memo(App);
